export const offsetCoordinates = (coordinates: [number, number][] | number[][], offsetBy: number): number[][] => {
    coordinates = removeSmallLoops(coordinates, offsetBy);

    const pts1 = [];

    const o = offsetBy;

    let p2l;

    let crit: boolean;
    let pli;
    let plim1;
    let p1lm1;
    let p2lm1;
    let thetam1;

    for (let i = 1; i < coordinates.length; i++) {
        const p1 = coordinates[i - 1];
        const p2 = coordinates[i];
        let theta = Math.atan2(p1[0] - p2[0], p1[1] - p2[1]);

        theta += Math.PI / 2;

        const dx = Math.round(o * Math.sin(theta));
        const dy = Math.round(o * Math.cos(theta));

        const p1l = [p1[0] + dx, p1[1] + dy];

        p2l = [p2[0] + dx, p2[1] + dy];

        if (i === 1) {
            // first point
            pts1.push(p1l);
            plim1 = p1l;
        } else {
            // mid coordinates
            if (theta === thetam1) {
                // adjacent segments in a straight line
                pts1.push(p1l);
                plim1 = p1l;
            } else {
                pli = getVectorIntersection(p1lm1, p2lm1, p1l, p2l);
                if (!pli) {
                    continue;
                }
                let lTheta = Math.atan2(plim1[0] - pli[0], plim1[1] - pli[1]);

                lTheta += Math.PI / 2;

                const dlxi = pli[0] - p1[0];
                const dlyi = pli[1] - p1[1];

                const mag = Math.sqrt(dlxi * dlxi + dlyi * dlyi);
                const dlxim = dlxi / mag;
                const dlyim = dlyi / mag;

                const dlxid = dlxim * (mag + o);
                const dlyid = dlyim * (mag + o);

                let dTheta = theta - thetam1;

                if (dTheta > (3 * Math.PI) / 2) {
                    dTheta -= Math.PI * 2;
                } else {
                    if (dTheta < -Math.PI / 2) {
                        dTheta += Math.PI * 2;
                    }
                }

                const lDTheta = thetam1 - lTheta;

                if (lDTheta > Math.PI / 2 || lDTheta < -Math.PI / 2) {
                    crit = false;
                } else {
                    crit = true;
                }

                if (crit) {
                    if (dTheta > Math.PI / 2 && dTheta < Math.PI) {
                        // intersect point on outside bend
                        pts1.push(p2lm1);
                        pts1.push([p1[0] + dlxid - dlxi, p1[1] + dlyid - dlyi]);
                        pts1.push(p1l);
                        plim1 = p1l;
                    } else {
                        pts1.push(pli);
                        plim1 = pli;
                    }
                }
            }
        }

        p1lm1 = p1l;
        p2lm1 = p2l;
        thetam1 = theta;
    }

    pts1.push(p2l); // final point

    return pts1;
};

function removeSmallLoops(coordinates: number[][], distance: number): number[][] {
    const newLine = [];

    for (let i = 0; i < coordinates.length - 1; i++) {
        const p0 = coordinates[i];
        const p1 = coordinates[i + 1];

        newLine.push(p0);

        for (let j = i + 2; j < coordinates.length - 1; j++) {
            const p2 = coordinates[j];
            const p3 = coordinates[j + 1];

            const intersection = getLineIntersection(p0, p1, p2, p3);

            if (!intersection) {
                continue;
            }

            let loopLength =
                Math.sqrt((p1[0] - intersection[0]) * (p1[0] - intersection[0]) + (p1[1] - intersection[1]) * (p1[1] - intersection[1])) +
                Math.sqrt((intersection[0] - p2[0]) * (intersection[0] - p2[0]) + (intersection[1] - p2[1]) * (intersection[1] - p2[1]));

            for (let k = i + 1; k < j; k++) {
                const pk = coordinates[k];
                const pl = coordinates[k + 1];

                loopLength += Math.sqrt((pl[0] - pk[0]) * (pl[0] - pk[0]) + (pl[1] - pk[1]) * (pl[1] - pk[1]));
            }

            // Remove loop if shorter than the circumference of a circle with radius twice the offset distance
            if (loopLength < Math.PI * 4 * distance) {
                newLine.push([intersection[0], intersection[1]]);
            } else {
                newLine.push(...coordinates.slice(i + 1, j + 1));
            }

            i = j;

            break;
        }
    }
    newLine.push(coordinates[coordinates.length - 1]);

    return newLine;
}

function getLineIntersection(p0, p1, p2, p3) {
    const s1X = p1[0] - p0[0];
    const s1Y = p1[1] - p0[1];
    const s2X = p3[0] - p2[0];
    const s2Y = p3[1] - p2[1];

    const s = (-s1Y * (p0[0] - p2[0]) + s1X * (p0[1] - p2[1])) / (-s2X * s1Y + s1X * s2Y);
    const t = (s2X * (p0[1] - p2[1]) - s2Y * (p0[0] - p2[0])) / (-s2X * s1Y + s1X * s2Y);
    if (s >= 0 && s <= 1 && t >= 0 && t <= 1) {
        // Collision detected
        const intX = p0[0] + t * s1X;
        const intY = p0[1] + t * s1Y;

        return [intX, intY];
    }
    return null; // No collision
}

function getVectorIntersection(p0, p1, p2, p3) {
    // this function computes the intersection of the sent lines p0-p1 and p2-p3
    // and returns the intersection point,

    let m1, m2; // the slopes of each line

    const x0 = p0[0];
    const y0 = p0[1];
    const x1 = p1[0];
    const y1 = p1[1];
    const x2 = p2[0];
    const y2 = p2[1];
    const x3 = p3[0];
    const y3 = p3[1];

    // compute slopes, note the cludge for infinity, however, this will
    // be close enough
    if (x1 - x0 !== 0) {
        m1 = (y1 - y0) / (x1 - x0);
    } else {
        m1 = 1e10;
    } // close enough to infinity

    if (x3 - x2 !== 0) {
        m2 = (y3 - y2) / (x3 - x2);
    } else {
        m2 = 1e10;
    } // close enough to infinity

    const a1 = m1;
    const a2 = m2;

    const b1 = -1;
    const b2 = -1;

    const c1 = y0 - m1 * x0;
    const c2 = y2 - m2 * x2;

    const det = a1 * b2 - a2 * b1;
    if (det === 0) {
        return null;
    }

    // compute the inverse of the determinate
    const detInv = 1 / det;

    // use Kramers rule to compute xi and yi
    const xi = (b1 * c2 - b2 * c1) * detInv;
    const yi = (a2 * c1 - a1 * c2) * detInv;

    return [Math.round(xi), Math.round(yi)];
}
